// Pure sort/filter helpers for the public partial-sales marketplace index.
// Kept free of React/intl so they can be unit-tested. All metrics are read from the
// PARTIAL SALE LISTING itself (listing.*), never the seller profile (listing.user.*).

// Sort options: one active metric + direction per selection. 'featured' = original order.
export const SORT_OPTIONS = [
  { value: 'featured',     field: null,      dir: null },
  { value: 'revenue_desc', field: 'revenue', dir: 'desc' },
  { value: 'revenue_asc',  field: 'revenue', dir: 'asc' },
  { value: 'aum_desc',     field: 'aum',     dir: 'desc' },
  { value: 'aum_asc',      field: 'aum',     dir: 'asc' },
  { value: 'clients_desc', field: 'clients', dir: 'desc' },
  { value: 'clients_asc',  field: 'clients', dir: 'asc' },
];

const num = (v) => parseFloat(v) || 0;

// Listing-level metric accessor. Revenue mirrors getTotalRevenue() (sum of the four listing
// revenue columns). parseFloat||0 coerces null/string; metrics are validated present on
// PartialSaleListing, so nulls are edge-only.
export const listingMetric = (listing, field) => {
  if (!listing) return 0;
  switch (field) {
    case 'revenue':
      return num(listing.fee_based_revenue) + num(listing.trails_revenue)
           + num(listing.direct_revenue) + num(listing.commission_revenue);
    case 'aum':
      return num(listing.assets_under_management);
    case 'clients':
      return num(listing.number_of_clients);
    default:
      return 0;
  }
};

// Distinct, trimmed, sorted union of states across the listings (null/empty stripped).
export const availableStates = (listings) => {
  const set = new Set();
  (listings || []).forEach((l) => (l && l.states ? l.states : []).forEach((s) => {
    const t = (s || '').trim();
    if (t) set.add(t);
  }));
  return Array.from(set).sort((a, b) => a.localeCompare(b));
};

// Multi-select state filter with OR semantics. Empty selection returns all. Listings with
// null/empty states match nothing when a filter is active.
export const filterByStates = (listings, selectedStates) => {
  if (!selectedStates || selectedStates.length === 0) return listings || [];
  const sel = new Set(selectedStates.map((s) => (s || '').trim()));
  return (listings || []).filter(
    (l) => (l && l.states ? l.states : []).some((s) => sel.has((s || '').trim()))
  );
};

// Sort by a listing metric. Stable tie-break on original index so order never jitters.
export const sortListings = (listings, field, dir) => {
  if (!field) return listings || [];
  const sign = dir === 'asc' ? 1 : -1;
  return (listings || [])
    .map((l, i) => [l, i])
    .sort((a, b) => {
      const diff = (listingMetric(a[0], field) - listingMetric(b[0], field)) * sign;
      return diff !== 0 ? diff : a[1] - b[1];
    })
    .map(([l]) => l);
};

// Filter first, then sort the survivors (deterministic, cheaper).
export const applySortFilter = (listings, { sortValue, selectedStates } = {}) => {
  const opt = SORT_OPTIONS.find((o) => o.value === sortValue) || SORT_OPTIONS[0];
  return sortListings(filterByStates(listings, selectedStates), opt.field, opt.dir);
};
